import React from 'react';
import mapValues from 'lodash/mapValues';
import invert from 'lodash/invert';
import zipObject from 'lodash/zipObject';
import clsx from 'clsx';
import CircleIcon from '@mui/icons-material/CircleRounded';
import Tag from 'core/components/Tag';

const valueOptions = ['off', 'error', 'pending', 'success', 'inprogress'] as const;
type ValueOption = typeof valueOptions[number];

type Part = 'root' | 'value';
type Props = {
  value: string;
  statusMap?: Partial<Record<ValueOption, string>>;
  classes?: Partial<Record<Part, string>>;
  className?: string;
};

const defaultStatusMap: Readonly<Record<ValueOption, string>> = Object.freeze(
  zipObject(valueOptions, valueOptions) as Record<ValueOption, string>,
);

const statusColors: Readonly<Record<ValueOption, string>> = Object.freeze(zipObject(
  valueOptions,
  ['text-gray-600', 'text-red-600', 'text-orange-400', 'text-emerald-700', 'text-amber-800'],
) as Record<ValueOption, string>);

const backgroundColors: Readonly<Record<ValueOption, string>> = Object.freeze(zipObject(
  valueOptions,
  ['bg-gray-100', 'bg-red-100', 'bg-orange-100', 'bg-emerald-100', '!bg-amber-100'],
) as Record<ValueOption, string>);

export default function StatusTag({
  value,
  statusMap: _statusMap,
  classes,
  className,
}: Props) {
  const statusMap = {
    ...defaultStatusMap,
    ...mapValues(_statusMap, (status) => status?.toLowerCase()),
  };
  const invertedStatuses = invert(statusMap);
  const statusOptionValue = invertedStatuses[value.toLowerCase()] as ValueOption;
  const color = statusColors[statusOptionValue];
  const backgroundColor = backgroundColors[statusOptionValue];
  return (
    <Tag
      className={clsx(backgroundColor, className, classes?.root)}
    >
      <div className={clsx(
        'flex items-center gap-2 font-semibold',
        color,
        classes?.value,
      )}
      >
        <CircleIcon className="!h-2.5 !w-2.5 !text-inherit" />
        {value}
      </div>
    </Tag>
  );
}
